import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Avatar,
  Checkbox,
  NotifyToggle,
  DeskButton,
  DeskEmpty,
  DeskLink,
  DeskModal,
  Field,
  STAGES,
  STAGE_LABELS,
  TYPE_LABELS,
  dateShort,
  inputStyle,
  relativeDay,
} from './essayShared';

// Pieces the shared desk's overview, deadlines and task screens have in
// common. The essay hub and the essay detail screen reuse the student's own
// components instead — those are the same screen for both viewers.

// Here an essay is only ever what the student chose to show, so "not started"
// is decided by the stage alone rather than by notes and tasks.
export function SharedStageBar({ status, wordCount }) {
  const started = !(status === 'BRAINSTORMING' && !wordCount);
  const filled = started ? STAGES.indexOf(status) + 1 : 0;
  const submitted = status === 'SUBMITTED';
  return (
    <div>
      <div style={{ display: 'flex', gap: 3 }}>
        {STAGES.map((_, i) => (
          <span key={i} style={{ height: 4, flex: 1, background: i < filled ? 'var(--college-ink)' : 'var(--college-border)' }} />
        ))}
      </div>
      <p
        style={{
          margin: '7px 0 0',
          fontSize: 11.5,
          fontWeight: 600,
          color: submitted ? 'var(--college-likely)' : started ? 'var(--college-ink)' : 'var(--college-muted-2)',
        }}
      >
        {started ? STAGE_LABELS[status] : 'Not started'}
      </p>
    </div>
  );
}

export function EssayTable({ essays, grant, grantId, onAddNote }) {
  if (essays.length === 0) {
    return (
      <div style={{ marginTop: 16 }}>
        <DeskEmpty
          title="Nothing shared yet"
          body={
            grant.seeProgress
              ? 'No essays are being tracked on this desk yet.'
              : `${grant.ownerName} hasn't shared essay progress with you.`
          }
        />
      </div>
    );
  }

  return (
    <div style={{ background: 'var(--college-panel)', border: '1px solid var(--college-border-soft)', borderTop: 'none' }}>
      {essays.map((essay, i) => (
        <div
          key={essay.id}
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0,1fr) 140px 120px 96px',
            gap: 16,
            alignItems: 'center',
            padding: '15px 18px',
            borderBottom: i === essays.length - 1 ? 'none' : '1px solid var(--college-border-soft)',
          }}
        >
          <div style={{ minWidth: 0 }}>
            <Link
              to={`/shared/${grantId}/essays/${essay.id}`}
              className="font-serif-editorial"
              style={{ fontSize: 17, color: 'var(--college-ink)', textDecoration: 'none' }}
            >
              {essay.title || TYPE_LABELS[essay.type] || 'Untitled essay'}
            </Link>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--college-muted)' }}>
              {[
                essay.school,
                TYPE_LABELS[essay.type],
                essay.wordCount != null && essay.wordLimit != null
                  ? `${essay.wordCount}/${essay.wordLimit} words`
                  : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
          </div>

          <SharedStageBar status={essay.status} wordCount={essay.wordCount} />

          <span
            style={{
              fontSize: 12,
              color: essay.waitingOn && essay.waitingOn !== 'NONE' ? 'var(--college-ink-soft)' : 'var(--college-muted-2)',
            }}
          >
            {essay.waitingOn === 'STUDENT'
              ? 'Waiting on them'
              : essay.waitingOn === 'ADVISOR'
              ? 'Waiting on advisor'
              : essay.waitingOn === 'PARENT'
              ? 'Waiting on a parent'
              : essay.deadline
              ? `Due ${dateShort(essay.deadline)}`
              : '—'}
          </span>

          <span style={{ textAlign: 'right' }}>
            {essay.draftShared && essay.draftUrl ? (
              <a
                href={essay.draftUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: 12, color: 'var(--college-red)', textDecoration: 'none' }}
              >
                Read draft ↗
              </a>
            ) : essay.draftShared ? (
              <span style={{ fontSize: 12, color: 'var(--college-muted-2)' }}>No draft yet</span>
            ) : grant.comment ? (
              <DeskLink onClick={() => onAddNote(essay)}>Add a note</DeskLink>
            ) : (
              <Link
                to={`/shared/${grantId}/essays/${essay.id}`}
                style={{ fontSize: 12, color: 'var(--college-red)', textDecoration: 'none' }}
              >
                Open →
              </Link>
            )}
          </span>
        </div>
      ))}
    </div>
  );
}

export function SharedTaskRow({ task, grantId, ownerName, onStatus }) {
  const done = task.status === 'DONE';
  const declined = task.status === 'DECLINED';
  const overdue = task.dueDate && new Date(task.dueDate) < Date.now() && !done && !declined;

  return (
    <div
      style={{
        display: 'flex',
        gap: 12,
        alignItems: 'flex-start',
        padding: '13px 0',
        borderBottom: '1px solid var(--college-border-soft)',
        opacity: done || declined ? 0.55 : 1,
      }}
    >
      <span style={{ marginTop: 2 }}>
        <Checkbox checked={done} onChange={() => onStatus(task, done ? 'TODO' : 'DONE')} label={task.title} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontSize: 14, color: 'var(--college-ink)', textDecoration: done ? 'line-through' : 'none' }}>
          {task.title}
        </p>
        <p style={{ margin: '4px 0 0', fontSize: 11.5, color: 'var(--college-muted)' }}>
          <Link to={`/shared/${grantId}/essays/${task.essayId}`} style={{ color: 'var(--college-muted)', textDecoration: 'none' }}>
            {task.context}
          </Link>
          {declined ? ' · you declined this' : ''}
          {!done && !declined && (
            <>
              {' · '}
              <DeskLink style={{ fontSize: 11.5 }} onClick={() => onStatus(task, 'DECLINED')}>
                decline
              </DeskLink>
              {ownerName && (
                <>
                  {' · '}
                  <DeskLink style={{ fontSize: 11.5 }} onClick={() => onStatus(task, done ? 'TODO' : 'DONE', { notify: true })}>
                    done &amp; email {ownerName.split(' ')[0]}
                  </DeskLink>
                </>
              )}
            </>
          )}
        </p>
      </div>
      <span
        className="font-mono-editorial"
        style={{
          fontSize: 11.5,
          color: overdue ? 'var(--college-red)' : task.dueDate ? 'var(--college-ink-soft)' : 'var(--college-muted-2)',
        }}
      >
        {task.dueDate ? dateShort(task.dueDate) : 'no date'}
      </span>
    </div>
  );
}

export function RecentBrainstormCard({ notes, essays, ownerName, onAddNote }) {
  return (
    <div style={{ background: 'var(--college-panel)', border: '1px solid var(--college-border-soft)' }}>
      {notes.length === 0 ? (
        <p style={{ margin: 0, padding: '13px 15px', fontSize: 12.5, color: 'var(--college-muted)' }}>
          Nothing yet. Your half-ideas are welcome here.
        </p>
      ) : (
        notes.map((note) => (
          <div key={note.id} style={{ padding: '13px 15px', borderBottom: '1px solid var(--college-border-soft)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Avatar person={note.author} size={22} />
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--college-ink)' }}>{note.author?.name}</span>
              <span className="font-mono-editorial" style={{ fontSize: 10.5, color: 'var(--college-muted-2)', marginLeft: 'auto' }}>
                {relativeDay(note.createdAt)}
              </span>
            </div>
            <p style={{ margin: '7px 0 0', fontSize: 12.5, lineHeight: 1.55, color: 'var(--college-ink-soft)' }}>
              {note.body.length > 84 ? `${note.body.slice(0, 84)}…` : note.body}
            </p>
          </div>
        ))
      )}
      {essays.length > 0 && (
        <div style={{ padding: '11px 15px' }}>
          <DeskLink onClick={() => onAddNote(essays[0])}>Add your own note →</DeskLink>
        </div>
      )}
      {notes.length === 0 && essays.length === 0 && (
        <p style={{ margin: 0, padding: '0 15px 13px', fontSize: 11.5, color: 'var(--college-muted-2)' }}>
          {ownerName} hasn't shared an essay you can post on yet.
        </p>
      )}
    </div>
  );
}

export function AddNoteModal({ essays, initialEssayId, ownerName, onClose, onPost }) {
  const [essayId, setEssayId] = useState(initialEssayId);
  const [body, setBody] = useState('');
  const [notify, setNotify] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSaving(true);
    try {
      await onPost(essayId, body, { notify });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to post that note');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DeskModal eyebrow={`For ${ownerName}`} title="Add a brainstorm note" onClose={onClose} width={520}>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <Field label="Which essay">
          <select style={inputStyle} value={essayId} onChange={(e) => setEssayId(e.target.value)}>
            {essays.map((essay) => (
              <option key={essay.id} value={essay.id}>
                {[essay.school, essay.title || TYPE_LABELS[essay.type]].filter(Boolean).join(' — ')}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Your note" hint="Everyone with comment access can see this — including their advisor">
          <textarea
            autoFocus
            style={{ ...inputStyle, minHeight: 110, resize: 'vertical', lineHeight: 1.55 }}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="A memory, a detail they've forgotten, a question…"
          />
        </Field>
        <div style={{ paddingTop: 16, borderTop: '1px solid var(--college-border)' }}>
          <NotifyToggle checked={notify} onChange={setNotify} who={[ownerName]} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
            <DeskButton onClick={onClose}>Cancel</DeskButton>
            <DeskButton variant="dark" type="submit" disabled={saving || !body.trim()}>
              {saving ? 'Posting…' : 'Post note'}
            </DeskButton>
          </div>
        </div>
      </form>
    </DeskModal>
  );
}
